import { unwrapResult } from "@reduxjs/toolkit";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Spin,
  Switch,
  message,
  Select,
} from "antd";
import React, { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { patchApi, postApi } from "../../redux/rest/restSlice";
import {
  LIST_ADVERTISEMENT,
  REGISTER_CABINET,
  UPDATE_APPROVED_CABINET,
  LIST_PRICING,
} from "../../constants/actionTypes";
import get from "lodash/get";
import { ORDER_PAYMENT_MODE } from "../../utilities/helper";

const { Option } = Select;

const AddEditApprovedCabinet = ({ modalInfo, setModalInfo, onSucces, tab }) => {
  const [loading, setLoading] = useState(false);
  const [advertisements, setAdvertisements] = useState([]);
  const [pricings, setPricings] = useState([]);
  const [isSubmitDisabled, setSubmitDisabled] = useState(false); // Track button disabled state
  const dispatch = useDispatch();
  const [form] = Form.useForm(); // ✅ Use Ant Design form instance

  const registerCabinet = (values) => {
    values.advertisement_id = values.advertisement_id || null;
    if (!values.pricing_id) {
      message.error("Please select Pricing");
      setLoading(false);
      return;
    }
    dispatch(
      postApi({
        endpoint: REGISTER_CABINET,
        payload: { ...values },
      })
    )
      .then(unwrapResult)
      .then((result) => {
        setLoading(false);
        message.success("Cabinet Added Successfully !!");
        onSucces("register");
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const updateCabinet = (values) => {
    values.advertisement_id = values.advertisement_id || null;
    if (!values.pricing_id) {
      message.error("Please select Pricing");
      setLoading(false);
      return;
    }
    if (values.kiosk_id === "") {
      values.kiosk_id = null;
    }
    if (values.sales_tax) {
      values.sales_tax = parseFloat(values.sales_tax);
    }
    const payload = {
      cabinet_id: get(modalInfo, "data.id"),
      ...values,
    };
    dispatch(
      patchApi({
        endpoint: UPDATE_APPROVED_CABINET,
        payload,
      })
    )
      .then(unwrapResult)
      .then((result) => {
        setLoading(false);
        message.success("Cabinet Updated Successfully !!");
        onSucces("edit");
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const fetchAdvertisements = useCallback(async () => {
    try {
      const result = await dispatch(
        postApi({ endpoint: LIST_ADVERTISEMENT, payload: {} })
      ).then(unwrapResult);
      if (result?.success && Array.isArray(result?.data?.data)) {
        setAdvertisements(result.data.data);
      } else {
        message.error("Failed to fetch advertisements.");
      }
    } catch (error) {
      message.error("Error fetching advertisements.");
    }
  }, [dispatch]);
  const fetchPricings = useCallback(async () => {
    try {
      const result = await dispatch(
        postApi({ endpoint: LIST_PRICING, payload: {} })
      ).then(unwrapResult);
      if (result?.success && Array.isArray(result?.data?.data)) {
        setPricings(result.data.data);
      } else {
        message.error("Failed to fetch pricing.");
      }
    } catch (error) {
      message.error("Error fetching pricing.");
    }
  }, [dispatch]);

  useEffect(() => {
    fetchAdvertisements();
    fetchPricings();
  }, [fetchAdvertisements, fetchPricings]);

  const onFinish = (values) => {
    setLoading(true);
    if (get(modalInfo, "data")) {
      updateCabinet(values);
    } else {
      registerCabinet(values);
    }
  };

  const onValuesChange = (_, allValues) => {
    const isSalesTaxValid = !allValues.sales_tax || !isNaN(allValues.sales_tax); // Optional validation
    setSubmitDisabled(!isSalesTaxValid);
  };

  return (
    <Spin spinning={loading}>
      <Form
        form={form} // ✅ Attach the form instance
        name="basic"
        layout="vertical"
        initialValues={
          get(modalInfo, "data")
            ? {
                hardware_id: get(modalInfo, "data.cabinet_sn"),
                kiosk_id: get(modalInfo, "data.kiosk_id"),
                location_id: get(modalInfo, "data.location_id"),
                stripe_key: get(modalInfo, "data.stripe_key"),
                name: get(modalInfo, "data.name"),
                is_active: get(modalInfo, "data.is_active"),
                address: get(modalInfo, "data.address"),
                sales_tax: get(modalInfo, "data.sales_tax"),
                advertisement_id: get(modalInfo, "data.advertisement_id", null), // ✅ Ensure undefined by default
                auto_restart: get(modalInfo, "data.auto_restart"),
                order_payment_mode: get(modalInfo, "data.order_payment_mode"),
                force_check_inventory: get(modalInfo, "data.force_check_inventory"),
                pricing_id: get(modalInfo, "data.pricing_id", null),
              }
            : {}
        }
        onFinish={onFinish}
        onValuesChange={onValuesChange} // Track field changes
        autoComplete="off"
      >
        <div>
          <Form.Item
            label="Hardware Id"
            name="hardware_id"
            className="form-item"
            rules={[
              {
                required: true,
                message: "Please enter the Hardware id",
              },
            ]}
          >
            <Input disabled={get(modalInfo, "data") ? true : false} />
          </Form.Item>
        </div>

        <div>
          <Form.Item
            label="Kiosk Id"
            name="kiosk_id"
            className="form-item"
            rules={[
              {
                required: false,
                message: "Please enter the Kiosk id",
              },
            ]}
          >
            <Input placeholder="Enter Kiosk ID" />
          </Form.Item>
        </div>

        <div>
          <Form.Item
            label="Sales Tax"
            name="sales_tax"
            className="form-item"
            rules={[
              {
                validator: (_, value) => {
                  if (value === undefined || value === null || value === "") {
                    return Promise.resolve(); // No validation if empty
                  }
                  if (isNaN(value)) {
                    return Promise.reject("Sales Tax must be a number");
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              placeholder="Enter Sales Tax"
              style={{ width: "100%" }}
            />
          </Form.Item>
        </div>

        <div>
          <Form.Item
            label="Name"
            name="name"
            rules={[
              {
                required: true,
                message: "Please enter the name",
              },
            ]}
          >
            <Input placeholder="Enter Name" />
          </Form.Item>
        </div>

        <div>
          <Form.Item label="Address" name="address">
            <Input placeholder="Enter Address" />
          </Form.Item>
        </div>

        <div>
          <Form.Item
            label="Location Id"
            name="location_id"
            className="form-item"
            rules={[
              {
                required: false,
                message: "Please enter the Location id",
              },
            ]}
          >
            <Input placeholder="Enter Location ID" />
          </Form.Item>
        </div>

        <div>
          <Form.Item
            label="Stripe Key"
            name="stripe_key"
            className="form-item"
            rules={[
              {
                required: false,
                message: "Please enter the Stripe Key",
              },
            ]}
          >
            <Input placeholder="Enter Stripe Key" />
          </Form.Item>
        </div>
        {/* ✅ Advertisement Dropdown with correct clearing behavior */}
        <Form.Item label="Advertisement" name="advertisement_id">
          <Select
            placeholder="Select Advertisement"
            allowClear
            onClear={() => form.setFieldsValue({ advertisement_id: null })}
            onChange={(value) =>
              form.setFieldsValue({ advertisement_id: value })
            }
          >
            {/* ✅ Add a default empty option explicitly if needed */}
            <Option key="empty" value={null}>
              None
            </Option>

            {/* ✅ Ensure every option has a unique key */}
            {advertisements.map((ad) => (
              <Option
                key={ad.id || `ad-${ad.advertisement_name}`}
                value={ad.id}
              >
                {ad.advertisement_name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Pricing"
          name="pricing_id"
          rules={[
            {
              required: true,
              message: "Please select Pricing",
            },
          ]}
        >
          <Select
            placeholder="Select Pricing"
            onChange={(value) => form.setFieldsValue({ pricing_id: value })}
          >
            {pricings.map((pricing) => (
              <Option
                key={pricing.id || `pricing-${pricing.pricing_name}`}
                value={pricing.id}
              >
                {pricing.pricing_name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {tab !== "unapproved" && (
          <div>
            <Form.Item label="Active" name="is_active" valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>
        )}

        {tab !== "unapproved" && (
          <div>
            <Form.Item
              label="Auto Restart"
              name="auto_restart"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </div>
        )}

        {tab !== "unapproved" && (
          <div>
            <Form.Item
              label="Force Check Inventory"
              name="force_check_inventory"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </div>
        )}
        
        <div>
          <Form.Item
            label="Order Payment Mode"
            name="order_payment_mode"
            rules={[
              {
                required: true,
                message: "Please select an Order Payment Mode",
              },
            ]}
          >
            <Select placeholder="Select Order Payment Mode">
              {Object.entries(ORDER_PAYMENT_MODE).map(([key, label]) => (
                <Option key={key} value={key}>
                  {label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        <div className="mt-20 d-flex cabinet-btn">
          <Form.Item>
            <Button
              className="default-secondary-btn"
              onClick={() => {
                setModalInfo();
              }}
            >
              Cancel
            </Button>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              className="default-primary-btn"
              disabled={isSubmitDisabled} // Disable button if Sales Tax is invalid
            >
              {get(modalInfo, "data") ? "Update" : "Add"}
            </Button>
          </Form.Item>
        </div>
      </Form>
    </Spin>
  );
};

export default AddEditApprovedCabinet;
